import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import sponsorsDataRaw from '../../../data/sponsors.json';
import { sanitizeStrictText, sanitizeText, isValidURL, validateLength } from '../../../utils/sanitize';

export default function SponsorsTab() {
    const [activeInnerTab, setActiveInnerTab] = useState('sponsors'); // 'sponsors', 'tiers', 'page'
    
    // Data states
    const [sponsors, setSponsors] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form states
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tRes, sRes, pRes] = await Promise.all([
                supabase.from('sponsor_tiers').select('*').order('order_idx', { ascending: true }),
                supabase.from('sponsors').select('*').order('created_at', { ascending: false }),
                supabase.from('sponsors_page_content').select('*').eq('page_name', 'sponsors-page').single()
            ]);
            
            if (!tRes.error) setTiers(tRes.data || []);
            if (!sRes.error) setSponsors(sRes.data || []);
            if (!pRes.error) setPageContent(pRes.data?.data || null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleImportJSON = async () => {
        if (!window.confirm("This will import data from sponsors.json into your database. Existing data might be duplicated. Proceed?")) return;
        
        try {
            // 1. Import Page Content
            const { error: pError } = await supabase.from('sponsors_page_content').upsert({
                page_name: 'sponsors-page',
                data: sponsorsDataRaw.section
            }, { onConflict: 'page_name' });
            if (pError) throw pError;

            // 2. Import Tiers and Sponsors
            for (let i = 0; i < sponsorsDataRaw.tiers.length; i++) {
                const tier = sponsorsDataRaw.tiers[i];
                
                // Insert Tier
                const { data: insertedTier, error: tError } = await supabase.from('sponsor_tiers').insert({
                    name: tier.name,
                    accentWord: tier.accentWord,
                    color: tier.color,
                    logoHeight: tier.logoHeight,
                    maxCols: tier.maxCols,
                    order_idx: i
                }).select().single();
                
                if (tError) throw tError;

                // Insert Sponsors for this tier
                if (tier.sponsors && tier.sponsors.length > 0) {
                    const spks = tier.sponsors.map(s => ({
                        tier_id: insertedTier.id,
                        name: s.name,
                        link: s.link,
                        logo: s.logo
                    }));
                    const { error: sError } = await supabase.from('sponsors').insert(spks);
                    if (sError) throw sError;
                }
            }

            alert("Import successful!");
            fetchData();
        } catch (err) {
            console.error(err);
            alert("Error importing data: " + err.message);
        }
    };

    // --- SPONSOR FUNCTIONS ---
    const handleAddSponsor = () => {
        setFormData({ id: null, type: 'sponsor', tier_id: tiers.length > 0 ? tiers[0].id : '', name: '', link: '', logo: '' });
        setIsEditing(true);
        setError(null);
        setUploadSuccess(false);
    };

    const handleEditSponsor = (s) => {
        setFormData({ ...s, type: 'sponsor' });
        setIsEditing(true);
        setError(null);
        setUploadSuccess(false);
    };

    const handleDeleteSponsor = async (id) => {
        if (!window.confirm("Are you sure you want to delete this sponsor?")) return;
        await supabase.from('sponsors').delete().eq('id', id);
        fetchData();
    };

    // --- TIER FUNCTIONS ---
    const handleAddTier = () => {
        setFormData({ id: null, type: 'tier', name: '', accentWord: '', color: '#FFFFFF', logoHeight: 90, maxCols: 3, order_idx: tiers.length });
        setIsEditing(true);
        setError(null);
    };

    const handleEditTier = (t) => {
        setFormData({ ...t, type: 'tier' });
        setIsEditing(true);
        setError(null);
    };

    const handleDeleteTier = async (id) => {
        if (!window.confirm("Delete this tier? All sponsors in this tier will also be deleted.")) return;
        await supabase.from('sponsor_tiers').delete().eq('id', id);
        fetchData();
    };

    // --- PAGE CONTENT FUNCTIONS ---
    const handleEditPage = () => {
        setFormData({ ...(pageContent || {}), type: 'page' });
        setIsEditing(true);
        setError(null);
    };

    // --- FORM SUBMISSION ---
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ── File type validation ──
        const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

        const fileExt = file.name.split('.').pop().toLowerCase();

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            setError(`Invalid file type "${file.type}". Allowed: JPEG, PNG, WebP, GIF, SVG.`);
            e.target.value = '';
            return;
        }

        if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
            setError(`Invalid file extension ".${fileExt}". Allowed: ${ALLOWED_EXTENSIONS.join(', ')}.`);
            e.target.value = '';
            return;
        }

        // ── Size validation ──
        if (file.size > 500 * 1024) {
            setError('Image must be under 500KB.');
            e.target.value = '';
            return;
        }

        // ── Filename sanitization ──
        const safeName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;

        setUploading(true);
        setError(null);
        setUploadSuccess(false);

        try {
            const { error: uploadError } = await supabase.storage
                .from('sponsor-images')
                .upload(safeName, file, {
                    contentType: file.type,
                    upsert: false,
                });

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('sponsor-images')
                .getPublicUrl(safeName);

            setFormData({ ...formData, logo: data.publicUrl });
            setUploadSuccess(true);
        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message || 'Failed to upload image. Did you create the sponsor-images bucket and set policies?');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (formData.type === 'sponsor') {
                // Validate
                const nameCheck = validateLength(formData.name, 100, 2);
                if (!nameCheck.valid) { setError(`Name: ${nameCheck.reason}`); return; }
                if (formData.link && !isValidURL(formData.link)) { setError('Website link must be a valid https:// URL.'); return; }

                const payload = {
                    tier_id: formData.tier_id,
                    name: sanitizeStrictText(formData.name, 100),
                    link: (formData.link || '').trim().slice(0, 2048),
                    logo: formData.logo || ''
                };
                if (formData.id) await supabase.from('sponsors').update(payload).eq('id', formData.id);
                else await supabase.from('sponsors').insert([payload]);
            } else if (formData.type === 'tier') {
                // Validate
                const nameCheck = validateLength(formData.name, 50, 2);
                if (!nameCheck.valid) { setError(`Tier name: ${nameCheck.reason}`); return; }
                const logoHeight = parseInt(formData.logoHeight);
                const maxCols = parseInt(formData.maxCols);
                if (isNaN(logoHeight) || logoHeight < 20 || logoHeight > 500) { setError('Logo height must be between 20 and 500.'); return; }
                if (isNaN(maxCols) || maxCols < 1 || maxCols > 6) { setError('Max cols must be between 1 and 6.'); return; }

                const payload = {
                    name: sanitizeStrictText(formData.name, 50),
                    accentWord: sanitizeStrictText(formData.accentWord || '', 50),
                    color: /^#[0-9A-Fa-f]{6}$/.test(formData.color) ? formData.color : '#FFFFFF',
                    logoHeight,
                    maxCols,
                    order_idx: Math.max(0, Math.min(99, parseInt(formData.order_idx) || 0))
                };
                if (formData.id) await supabase.from('sponsor_tiers').update(payload).eq('id', formData.id);
                else await supabase.from('sponsor_tiers').insert([payload]);
            } else if (formData.type === 'page') {
                const payload = { ...formData };
                delete payload.type;
                // Sanitize all text fields
                for (const key of Object.keys(payload)) {
                    if (typeof payload[key] === 'string' && key !== 'ctaTiers') {
                        payload[key] = sanitizeText(payload[key]).slice(0, 500);
                    }
                }
                if (typeof payload.ctaTiers === 'string') {
                    payload.ctaTiers = payload.ctaTiers.split(',').map(s => sanitizeStrictText(s.trim(), 50));
                }
                const { error: pError } = await supabase.from('sponsors_page_content').upsert({
                    page_name: 'sponsors-page',
                    data: payload
                }, { onConflict: 'page_name' });
                if (pError) throw pError;
            }

            setIsEditing(false);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <div className="text-white">Loading sponsors...</div>;

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>
                        {formData.id ? `Edit ${formData.type}` : `Add New ${formData.type}`}
                    </h4>
                </div>
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <form onSubmit={handleSubmit}>
                        
                        {formData.type === 'sponsor' && (
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Company Name</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Tier</label>
                                    <select className="form-control bg-dark text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.tier_id} onChange={e => setFormData({...formData, tier_id: e.target.value})}>
                                        {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Website Link</label>
                                    <input type="url" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.link} onChange={e => setFormData({...formData, link: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Logo (Transparent PNG/SVG, Max 500KB, Rec: 400x200px)</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.svg" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} onChange={handleImageUpload} disabled={uploading} />
                                        {uploading && <div className="spinner-border spinner-border-sm text-light" role="status"></div>}
                                    </div>
                                    {uploadSuccess && (
                                        <div className="mt-2 text-success" style={{ fontSize: '12px' }}>
                                            <i className="fas fa-check-circle me-1"></i> Image uploaded successfully
                                        </div>
                                    )}
                                    {formData.logo && !uploadSuccess && (
                                        <div className="mt-2">
                                            <a href={formData.logo} target="_blank" rel="noreferrer" style={{ color: 'var(--eventflow-primary)', fontSize: '12px', textDecoration: 'none' }}>
                                                View current logo <i className="fas fa-external-link-alt ms-1"></i>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {formData.type === 'tier' && (
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Tier Name</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Accent Word (The word that should be colored)</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.accentWord} onChange={e => setFormData({...formData, accentWord: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Color (Hex)</label>
                                    <input type="color" className="form-control form-control-color bg-transparent" value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Logo Height (px)</label>
                                    <input type="number" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.logoHeight} onChange={e => setFormData({...formData, logoHeight: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Max Cols (1-4)</label>
                                    <input type="number" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required min="1" max="4" value={formData.maxCols} onChange={e => setFormData({...formData, maxCols: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Order Index</label>
                                    <input type="number" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.order_idx} onChange={e => setFormData({...formData, order_idx: e.target.value})} />
                                </div>
                            </div>
                        )}

                        {formData.type === 'page' && (
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Eyebrow</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.eyebrow} onChange={e => setFormData({...formData, eyebrow: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Title Year</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.titleYear} onChange={e => setFormData({...formData, titleYear: e.target.value})} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Title Accent</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.titleAccent} onChange={e => setFormData({...formData, titleAccent: e.target.value})} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Subtitle</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={formData.subtitle} onChange={e => setFormData({...formData, subtitle: e.target.value})}></textarea>
                                </div>
                                <hr className="my-4 border-secondary" />
                                <h5>CTA Section</h5>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.ctaTitle} onChange={e => setFormData({...formData, ctaTitle: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.ctaButtonText} onChange={e => setFormData({...formData, ctaButtonText: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Subtitle</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={formData.ctaSubtitle} onChange={e => setFormData({...formData, ctaSubtitle: e.target.value})}></textarea>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Tiers (Comma separated)</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={Array.isArray(formData.ctaTiers) ? formData.ctaTiers.join(', ') : formData.ctaTiers} onChange={e => setFormData({...formData, ctaTiers: e.target.value})}></textarea>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 d-flex gap-2">
                            <button type="submit" className="btn text-white" style={{ background: 'var(--eventflow-primary)' }}>Save Changes</button>
                            <button type="button" className="btn btn-outline-secondary text-white" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Manage Sponsors</h4>
                {tiers.length === 0 && <button className="btn btn-warning btn-sm" onClick={handleImportJSON}><i className="fas fa-file-import me-2"></i>Import from JSON</button>}
            </div>
            
            <div className="card-body px-4 pb-4">
                <ul className="nav nav-pills mb-4" style={{ gap: '10px' }}>
                    <li className="nav-item">
                        <button className={`nav-link ${activeInnerTab === 'sponsors' ? 'active bg-primary' : 'bg-transparent text-white'}`} style={{ border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setActiveInnerTab('sponsors')}>Sponsors List</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${activeInnerTab === 'tiers' ? 'active bg-primary' : 'bg-transparent text-white'}`} style={{ border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setActiveInnerTab('tiers')}>Manage Tiers</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${activeInnerTab === 'page' ? 'active bg-primary' : 'bg-transparent text-white'}`} style={{ border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setActiveInnerTab('page')}>Page Content</button>
                    </li>
                </ul>

                {activeInnerTab === 'sponsors' && (
                    <>
                        <button className="btn mb-4 text-white" style={{ background: 'var(--eventflow-base)' }} onClick={handleAddSponsor}>
                            <i className="fas fa-plus me-2"></i> Add New Sponsor
                        </button>
                        <div className="table-responsive">
                            <table className="table table-dark table-hover align-middle mb-0" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Logo</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Name</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Tier</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sponsors.map(s => (
                                        <tr key={s.id}>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                {s.logo ? <img src={s.logo} alt={s.name} style={{ height: '40px', borderRadius: '4px', objectFit: 'contain', background: '#fff', padding: '2px' }} /> : '-'}
                                            </td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white fw-bold">{s.name}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white-50">
                                                {tiers.find(t => t.id === s.tier_id)?.name || 'Unknown Tier'}
                                            </td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'right' }}>
                                                <button className="btn btn-sm btn-outline-light me-2" onClick={() => handleEditSponsor(s)}><i className="fas fa-edit"></i></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteSponsor(s.id)}><i className="fas fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {sponsors.length === 0 && (
                                        <tr><td colSpan="4" className="text-center text-white-50 py-4" style={{ background: 'transparent' }}>No sponsors found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {activeInnerTab === 'tiers' && (
                    <>
                        <button className="btn mb-4 text-white" style={{ background: 'var(--eventflow-base)' }} onClick={handleAddTier}>
                            <i className="fas fa-plus me-2"></i> Add New Tier
                        </button>
                        <div className="table-responsive">
                            <table className="table table-dark table-hover align-middle mb-0" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Order</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Name</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Color</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Specs</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tiers.map(t => (
                                        <tr key={t.id}>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white-50">{t.order_idx}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white fw-bold">{t.name}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: t.color, borderRadius: '4px', border: '1px solid rgba(255,255,255,0.2)' }}></span>
                                            </td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white-50">Height: {t.logoHeight}px | Cols: {t.maxCols}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'right' }}>
                                                <button className="btn btn-sm btn-outline-light me-2" onClick={() => handleEditTier(t)}><i className="fas fa-edit"></i></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTier(t.id)}><i className="fas fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {tiers.length === 0 && (
                                        <tr><td colSpan="5" className="text-center text-white-50 py-4" style={{ background: 'transparent' }}>No tiers found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {activeInnerTab === 'page' && (
                    <div className="text-center py-5">
                        <i className="fas fa-file-alt text-white-50 mb-3" style={{ fontSize: '48px' }}></i>
                        <h5 className="text-white">Page Content</h5>
                        <p className="text-white-50 mb-4">Edit the titles and Call-To-Action text shown on the sponsors section.</p>
                        <button className="btn text-white" style={{ background: 'var(--eventflow-base)' }} onClick={handleEditPage}>
                            <i className="fas fa-edit me-2"></i> Edit Page Content
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
